/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-pascal-case */
import { useEffect, useState } from "react";
import {
  MRT_TableContainer,
  MRT_TableOptions,
  MRT_TablePagination,
  useMaterialReactTable,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
} from "material-react-table";
import { TextField, Text } from "@techzyapp/ui-lib";
import { useTableFilter } from "./useTableFilter";
import TableFilter from "./TableFilter";
import TableExport, { TableExportProps } from "./TableExport";
import { tableIcons } from "./TableIcons";
import {
  columnPinning,
  selectProps,
  rowsPerPageOptions,
  muiCircularProgressProps,
  muiTableBodyCellProps,
  muiTableHeadCellProps,
  renderEmptyRowsFallback,
  rowsPerPageOptionsWithoutAll,
} from "./TableOptions";

import "./styles.css";

interface ServerTableProps<T extends object> extends MRT_TableOptions<T> {
  dataCount?: number;
  fetch: (id?: string) => Promise<void>;
  loading?: boolean;
  enableFilter?: boolean;
  enableSearch?: boolean;
  disableAllSort?: boolean;
  hiddenColumns?: any;
  enableExport?: boolean;
  exportProps?: TableExportProps;
  preserveQueryKeys?: string[];
}

const ServerTable = <T extends object>({
  dataCount,
  fetch,
  loading = false,
  enableFilter = true,
  enableSearch = true,
  disableAllSort = true,
  hiddenColumns,
  enableExport = false,
  exportProps,
  preserveQueryKeys,
  ...props
}: ServerTableProps<T>) => {
  const {
    initialPage,
    initialSize,
    initialSearch,
    initialFilters,
    setPagination,
    setSearch,
    setFilters,
  } = useTableFilter(preserveQueryKeys);

  const [columnFilters, setColumnFilters] =
    useState<MRT_ColumnFiltersState>(initialFilters);
  const [globalSearch, setGlobalSearchState] = useState(initialSearch);
  const [rowCount, setRowCount] = useState<number>(0);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [paging, setPaging] = useState<MRT_PaginationState>({
    pageIndex: initialPage - 1,
    pageSize: initialSize,
  });

  const [searchState, setSearchState] = useState(initialSearch);

  useEffect(() => {
    setRowCount(dataCount || 0);
  }, [dataCount]);

  useEffect(() => {
    setSearch(globalSearch);
    setFilters(columnFilters);
    fetch().catch();
  }, [paging.pageIndex, paging.pageSize, columnFilters, globalSearch]);

  const resetPagination = () => {
    setPagination(1, paging.pageSize);
    setPaging((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const table = useMaterialReactTable({
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      columnPinning: columnPinning,
      columnVisibility: {
        ...hiddenColumns,
      },
    },
    enableColumnPinning: true,
    enableRowActions: true,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    paginationDisplayMode: "pages",
    muiPaginationProps: {
      onChange: (event, newPage) => {
        setPagination(newPage, paging.pageSize);
        setPaging((prev) => ({
          ...prev,
          pageIndex: newPage - 1,
        }));
      },
      SelectProps: {
        onChange: (e) => {
          const newSize = Number(e.target.value);
          setPagination(1, newSize);
          setPaging({
            pageIndex: 0,
            pageSize: newSize,
          });
        },
        ...selectProps,
      },
      rowsPerPageOptions: disableAllSort
        ? rowsPerPageOptionsWithoutAll
        : rowsPerPageOptions,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalSearchState,
    onSortingChange: setSorting,
    columnFilterDisplayMode: "custom",
    enableFacetedValues: true,
    rowCount,
    state: {
      columnFilters,
      globalFilter: globalSearch,
      isLoading: loading,
      pagination: paging,
      sorting,
    },
    enableSorting: false,
    muiCircularProgressProps,
    muiTableBodyCellProps,
    muiTableHeadCellProps,
    renderEmptyRowsFallback,
    icons: tableIcons,
    ...props,
  });

  return (
    <div className={table.getPageCount() <= 1 ? "hide-pagination" : ""}>
      {enableFilter || enableSearch || enableExport ? (
        <div className="Mui-tableHeader">
          <div className="Mui-tableHeader-left">
            {enableSearch && (
              <TextField
                fullWidth
                placeholder="Search"
                iconButtonName="SearchNormal"
                adornmentPosition="start"
                value={searchState}
                onIconButtonClick={() => {
                  setGlobalSearchState(searchState);
                  resetPagination();
                }}
                onChange={(e) => {
                  !e.target.value && setGlobalSearchState("");
                  setSearchState(e.target.value);
                }}
              />
            )}
          </div>
          <div className="Mui-tableHeader-right">
            {enableExport && (
              <TableExport
                data={props.data}
                filename={exportProps?.filename}
                currentPage={exportProps?.currentPage}
                email={exportProps?.email}
              />
            )}
            {enableFilter && (
              <TableFilter
                table={table}
                columnFilters={columnFilters}
                resetPagination={resetPagination}
              />
            )}
          </div>
        </div>
      ) : null}
      <MRT_TableContainer table={table} />
      {dataCount && dataCount > 0 ? (
        <div className="Mui-tableFooter-totalRowsWrapper">
          <Text>Total Rows: {dataCount.toLocaleString()}</Text>
        </div>
      ) : null}
      <MRT_TablePagination table={table} />
    </div>
  );
};

export default ServerTable;
